// apps/merchant-v3/src/App.tsx
//
// Phase 6 Wave 1 Task 02 — Wires AppShell (Sidebar + TopBar +
// SuspendBanner) around the lazy-loaded route map. BrowserRouter is
// already provided by main.tsx so this file only composes the shell +
// router.
//
// Phase 6 Wave 1 Task 03 — Wraps the SPA in <AuthProvider> and renders
// the AppShell conditionally. When the user is unauthenticated (or the
// `useMe()` bootstrap is still pending and we are already on /login),
// we render <AppRouter /> without the sidebar/topbar chrome so the
// LoginPage / ForgotPasswordPage fill the full viewport. Authenticated
// routes render inside the AppShell as before.
//
// Phase 6 Wave 2 Task 04 — Fires `checkBEPrereqs()` once on mount so the
// `<BEPrereqsBanner />` (mounted in AppShell) can flip to visible if
// any of the cross-phase P1/P3/P5 endpoints probe back as 404. The probe
// is fire-and-forget; failures are logged via console.warn from inside
// prereqCheck.ts.
//
// Phase 6 Wave 2 Task 06 — Wraps the SPA in <ThemeProvider> +
// <I18nProvider> so every descendant sees:
//   - i18next bootstrap (EN/VI/FR locales, namespace lazy-loading)
//   - Tailwind 4 dark-mode class on <html>, driven by useThemeStore
//
// Provider order (outer → inner):
//   <ThemeProvider>          // applies html.dark, owns matchMedia
//     <I18nProvider>         // wires <I18nextProvider i18n={...}>
//       <AuthProvider>       // user mirror + 401 navigate handler
//         <ShellSwitch>      // public-route bypass for /login etc.
//
// Phase 6.1 Wave 5 Task 23 — <ScopeVersionProvider> mounted inside the
// authenticated shell so the P6.1.D-21 scopeVersion interceptor (registered
// against the api/client) can invalidate the TanStack cache + toast when a
// member's store access changes mid-session (assignment edit / team
// deactivate). It sits inside QueryClientProvider (main.tsx) + Toaster +
// I18nProvider so useQueryClient / useToast / useTranslation all resolve.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-03
// vg-binding: PLAN:task-04
// vg-binding: PLAN:task-06
// vg-binding: PLAN:task-23
// vg-binding: P6.D-02
// vg-binding: P6.D-03
// vg-binding: P6.D-05
// vg-binding: P6.D-131
// vg-binding: P6.D-205
// vg-binding: P6.D-221
// vg-binding: P6.D-235
// vg-binding: P6.D-248
// vg-binding: P6.1.D-21

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Toaster } from '@printway/ui';

import { AppShell } from '@/layouts/AppShell';
import { AppRouter } from '@/router';
import { AuthProvider, useAuth } from '@/lib/auth/AuthProvider';
import { checkBEPrereqs } from '@/lib/api/prereqCheck';
import { I18nProvider } from '@/lib/i18n/I18nProvider';
import { ScopeVersionProvider } from '@/lib/scope/ScopeVersionProvider';
import { ThemeProvider } from '@/lib/theme/ThemeProvider';
// Phase 6 Wave 15b Task 58 — centralised WS subscription
// provider for authenticated pages. Mounts inside <AuthProvider> so
// `useAuth()` resolves; mounts AROUND <AppShell> so every authenticated
// route sees `useWsEvent`.
import { WsProvider } from '@/lib/websocket/WsProvider';
import { MerchantSupportRealtime } from '@/features/support/supportRealtime';

/** Set of public routes — when current path matches OR user is null,
 *  AppShell chrome is suppressed so the page renders full-bleed. */
const PUBLIC_PATHS = new Set<string>([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/register',
]);

function ShellSwitch(): JSX.Element {
  const { user } = useAuth();
  const location = useLocation();
  const isPublicPath = PUBLIC_PATHS.has(location.pathname);

  useEffect(() => {
    if (!user) return;
    // Dev only. These probes exist to notice if the cross-phase P1/P3/P5
    // endpoints answer 404 during a migration, and they cost three requests
    // on every signed-in session: GET /auth/me (a second one — useMe already
    // fetched it), /wallet/fx-rates and /sync-logs?limit=1. All three have
    // long since shipped and answer 200, so in production the probes only
    // confirm that permanent endpoints are still there. The banner still works
    // locally, where a missing endpoint is something you can act on.
    if (!import.meta.env.DEV) return;
    void checkBEPrereqs().catch(() => {
      // Already logged inside checkBEPrereqs; swallow here so a probe
      // network blip never breaks the app shell render.
    });
  }, [user]);

  // Suppress chrome on public auth routes OR while bootstrap is still
  // pending and we have no user yet — the inner LoadingShell or
  // LoginPage covers the screen on its own.
  if (isPublicPath || !user) {
    return <AppRouter />;
  }

  return (
    <ScopeVersionProvider>
      <WsProvider>
        <AppShell>
          <MerchantSupportRealtime />
          <AppRouter />
        </AppShell>
      </WsProvider>
    </ScopeVersionProvider>
  );
}

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <Toaster>
            <ShellSwitch />
          </Toaster>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
